import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, CalendarCheck } from "lucide-react";
import { Logo } from "./Logo";
import { useClinic } from "../context/ClinicContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/diagnostics", label: "X-Ray & Diagnostics" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { phone } = useClinic();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 border-b ${
        scrolled ? "bg-white/95 backdrop-blur-md border-slate-200 shadow-sm" : "bg-white/90 backdrop-blur-md border-slate-100"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-20 lg:h-24">
        <Link to="/" data-testid="nav-logo-link">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold rounded-lg transition-colors ${
                  isActive ? "text-[#1A75BC] bg-[#E8F4FD]" : "text-[#334E68] hover:text-[#1A75BC] hover:bg-slate-50"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href={`tel:${phone}`} className="flex items-center gap-2 text-sm font-semibold text-[#1A75BC]" data-testid="nav-call">
            <Phone size={16} /> {phone}
          </a>
          <Link
            to="/contact"
            data-testid="nav-book-btn"
            className="gradient-bg btn-glow text-white text-sm font-bold px-5 py-2.5 rounded-full flex items-center gap-2"
          >
            <CalendarCheck size={16} /> Book Appointment
          </Link>
        </div>

        <button
          className="lg:hidden p-2 text-[#0F1E36]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-1" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-lg font-semibold ${isActive ? "text-[#1A75BC] bg-[#E8F4FD]" : "text-[#334E68]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/contact" className="gradient-bg text-white font-bold px-4 py-3 rounded-lg flex items-center justify-center gap-2 mt-2">
            <CalendarCheck size={18} /> Book Appointment
          </Link>
        </div>
      )}
    </header>
  );
};

export default Navbar;
